import { Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Stack, IconButton, MenuItem, Switch, Typography } from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import React from "react";
import { CenterType } from '../../../../services/ConfigurationService/types/centerType';
import { useUpdateCenterMutation, useGetCenterTypesQuery } from '../../../../services/ConfigurationService';

interface EditCenterDialogProps {
  center: CenterType;
  onSave?: (updated: Partial<CenterType>) => void;
  onClick?: (center: CenterType) => void;
}

type CenterForm = Partial<CenterType>;
type FieldHandler = (form: CenterForm, center: CenterType, value: string, checked: boolean) => CenterForm;

const nestedTextField = (section: keyof CenterType, field: string): FieldHandler =>
  (form, center, value) => ({
    [section]: {
      ...(center[section] as object ?? {}),
      ...(form[section] as object ?? {}),
      [field]: value,
    },
  });

const nestedBooleanField = (section: keyof CenterType, field: string): FieldHandler =>
  (form, center, _value, checked) => ({
    [section]: {
      ...(center[section] as object ?? {}),
      ...(form[section] as object ?? {}),
      [field]: checked,
    },
  });

const FIELD_HANDLERS: Record<string, FieldHandler> = {
  providerNumber: nestedTextField('medicareConfig', 'providerNumber'),
  isCriticalAccess: nestedBooleanField('medicareConfig', 'isCriticalAccess'),
  taxId: nestedTextField('govtIdent', 'taxId'),
  npi: nestedTextField('govtIdent', 'npi'),
  systemId: nestedTextField('attributes', 'systemId'),
  dimensionName: nestedTextField('attributes', 'name'),
  address1: nestedTextField('address', 'address1'),
  address2: nestedTextField('address', 'address2'),
  city: nestedTextField('address', 'city'),
  state: nestedTextField('address', 'state'),
  zip: nestedTextField('address', 'zip'),
  country: nestedTextField('address', 'country'),
};

export function EditCenterDialog({ center, onSave, onClick }: EditCenterDialogProps) {
  const [open, setOpen] = React.useState(false);
  const [form, setForm] = React.useState<Partial<CenterType>>({});
  const [updateCenter, { isLoading }] = useUpdateCenterMutation();
  const { data: centerTypes, isLoading: centerTypesLoading } = useGetCenterTypesQuery();
  const [success, setSuccess] = React.useState(false);

  React.useEffect(() => {
    setForm(center);
  }, [center]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    const update = FIELD_HANDLERS[name]?.(form, center, value, type === 'checkbox' && checked)
      ?? { [name]: value };
    setForm({ ...form, ...update });
  };

  const handleSwitch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, isInactive: e.target.checked });
  };

  const handleSave = async () => {
    // Ensure all required fields are present for updateCenter
    const payload: any = {
      ...center,
      ...form,
      medicareConfig: { ...center.medicareConfig, ...form.medicareConfig },
      govtIdent: { ...center.govtIdent, ...form.govtIdent },
      address: { ...center.address, ...form.address },
      attributes: { ...center.attributes, ...form.attributes }
    };
    await updateCenter(payload);
    setSuccess(true);
    setTimeout(() => setSuccess(false), 2000);
    if (onSave) onSave(payload);
    setOpen(false);
  };

  const handleClick = () => {
    if (onClick) {
      onClick(center);
    } else {
      setOpen(true);
    }
  };

  return (
    <>
      <IconButton size="small" onClick={handleClick} aria-label="Edit Center Details">
        <EditIcon fontSize="small" />
      </IconButton>
      {onSave && (
        <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Center Details</DialogTitle>
          <DialogContent>
            <Stack gap={2} sx={{ marginTop: '5px' }}>
              <TextField label='Name' name='name' value={form.name ?? center.name} onChange={handleChange} fullWidth size='small' sx={{ width: '500px' }} />
              <TextField label='Short Name' name="shortName" value={form.shortName ?? center.shortName} onChange={handleChange} fullWidth size='small' sx={{ width: '500px' }} />
            </Stack>
            <Typography variant='h6' sx={{ marginTop: '10px', marginBottom: '5px' }}>Attributes</Typography>
            <Stack direction='row' gap={2}>
              <TextField label='PAS' name='pas' value={form.pas ?? center.pas} onChange={handleChange} size='small' sx={{ width: '110px' }} />
              <TextField label='Type' select name='centerType' value={form.centerType || ''} onChange={handleChange} size='small' disabled={centerTypesLoading}>
                {(centerTypes || []).map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.name}</MenuItem>
                ))}
              </TextField>
              <TextField label='NPI' name='npi' value={form.govtIdent?.npi ?? center.govtIdent.npi} onChange={handleChange} size='small' InputLabelProps={{ shrink: true }} sx={{ width: '125px' }} />
              <TextField label='Tax ID' name='taxId' value={form.govtIdent?.taxId ?? center.govtIdent.taxId} onChange={handleChange} size='small' InputLabelProps={{ shrink: true }} sx={{ width: '125px' }} />
            </Stack>
            <Stack direction='row' gap={2} sx={{ marginTop: '10px' }}>
              <TextField label='System ID' name='systemId' value={form.attributes?.systemId ?? center.attributes?.systemId ?? ''} onChange={handleChange} size='small' sx={{ width: '160px' }} />
              <TextField label='Dim.Facility Name' name='dimensionName' value={form.attributes?.name ?? center.attributes?.name ?? ''} onChange={handleChange} size='small' fullWidth />
            </Stack>
            <Typography variant='h6' sx={{ marginTop: '10px', marginBottom: '5px' }}>Address</Typography>
            <Stack gap={1}>
              <TextField label='Address 1' name='address1' value={form.address?.address1 ?? center.address.address1} onChange={handleChange} size='small' fullWidth />
              <TextField label='Address 2' name='address2' value={form.address?.address2 ?? center.address.address2} onChange={handleChange} size='small' fullWidth />
              <Stack direction='row' gap={2}>
                <TextField label='City' name='city' value={form.address?.city ?? center.address.city} onChange={handleChange} size='small' sx={{ width: '220px' }} />
                <TextField label='State' name='state' value={form.address?.state ?? center.address.state} onChange={handleChange} size='small' sx={{ width: '120px' }} />
                <TextField label='Zip' name='zip' value={form.address?.zip ?? center.address.zip} onChange={handleChange} size='small' sx={{ width: '120px' }} />
              </Stack>
            </Stack>
            <Stack direction='row' alignItems='center' gap={1} sx={{ marginTop: '10px' }}>
              <Typography>Inactive Center</Typography>
              <Switch checked={!!form.isInactive} onChange={handleSwitch} />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} variant="contained">Save</Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
